import React from "react";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { AI_REPORT_PARAM_INPUT_TYPES, normalizeAiReportParamOptions } from "../../utils/aiReportTypes";
import { serializeOptions } from "./systemConfigurationUtils";

const ReportsConfigPanel = ({
  t,
  mode,
  setMode,
  downloadModes,
  aiReportAvailability,
  updateAiReportAvailability,
  reportTypes,
  selectedType,
  selectedTypeIndex,
  setSelectedTypeIndex,
  updateType,
  addType,
  deleteType,
  updateParam,
  addParam,
  deleteParam,
}) => (
  <>
    <section className="system-config-panel">
      <div className="system-config-panel-head">
        <div>
          <h3>{t.healthTitle}</h3>
          <p>{t.healthDescription}</p>
        </div>
      </div>

      <div className="system-config-options" role="radiogroup" aria-label={t.healthTitle}>
        {Object.entries(downloadModes).map(([key, option]) => {
          const selected = mode === key;
          return (
            <button
              type="button"
              key={key}
              className={`system-config-option ${selected ? "selected" : ""}`}
              onClick={() => setMode(key)}
              role="radio"
              aria-checked={selected}
            >
              <span className="system-config-option-icon">{option.icon}</span>
              <span>
                <strong>{option.title}</strong>
                <small>{option.description}</small>
              </span>
              <span className="system-config-radio" />
            </button>
          );
        })}
      </div>
    </section>

    <section className="system-config-panel">
      <div className="system-config-panel-head">
        <div>
          <h3>{t.aiAvailabilityTitle}</h3>
          <p>{t.aiAvailabilityDescription}</p>
        </div>
      </div>

      <div className="system-config-switch-grid">
        {["devices", "dashboards", "templates"].map((scope) => (
          <label className="system-config-switch-card" key={scope}>
            <span>
              <strong>{t.aiAvailabilityScopes?.[scope] || scope}</strong>
              <small>{t.aiAvailabilityScopeDescriptions?.[scope] || ""}</small>
            </span>
            <input
              type="checkbox"
              checked={aiReportAvailability[scope] !== false}
              onChange={(event) => updateAiReportAvailability(scope, event.target.checked)}
            />
          </label>
        ))}
      </div>
    </section>

    <section className="system-config-panel">
      <div className="system-config-panel-head">
        <div>
          <h3>{t.aiTypesTitle}</h3>
          <p>{t.aiTypesDescription}</p>
        </div>
        <button type="button" className="system-config-add-btn" onClick={addType}>
          <AddIcon /> {t.addType}
        </button>
      </div>

      <div className="system-config-report-layout">
        <div className="system-config-type-list">
          {reportTypes.length === 0 ? (
            <div className="system-config-type-empty">{t.noTypes}</div>
          ) : reportTypes.map((type, index) => (
            <button
              type="button"
              key={`${type.value}-${index}`}
              className={`system-config-type-row ${selectedTypeIndex === index ? "selected" : ""}`}
              onClick={() => setSelectedTypeIndex(index)}
            >
              <strong>{type.label || t.untitledType}</strong>
              <small>{type.value || t.missingKey}</small>
            </button>
          ))}
        </div>

        {selectedType ? (
          <div className="system-config-type-editor">
            <div className="system-config-editor-grid">
              <label>
                <span>{t.typeKey}</span>
                <input value={selectedType.value} onChange={(e) => updateType(selectedTypeIndex, { value: e.target.value })} placeholder="power-meter" />
              </label>
              <label>
                <span>{t.displayName}</span>
                <input value={selectedType.label} onChange={(e) => updateType(selectedTypeIndex, { label: e.target.value })} placeholder="Power Meter" />
              </label>
              <label className="system-config-wide">
                <span>{t.reportApi}</span>
                <input value={selectedType.apiUrl} onChange={(e) => updateType(selectedTypeIndex, { apiUrl: e.target.value })} placeholder="/pyv1/reports/power-meter/upload-pdf" />
              </label>
            </div>

            <div className="system-config-param-head">
              <h4>{t.parameters}</h4>
              <button type="button" className="system-config-add-btn secondary" onClick={() => addParam(selectedTypeIndex)}>
                <AddIcon /> {t.addParam}
              </button>
            </div>

            <div className="system-config-param-list">
              {selectedType.params.map((param, paramIndex) => (
                <div className="system-config-param-row" key={`${param.key}-${paramIndex}`}>
                  <input value={param.key} onChange={(e) => updateParam(selectedTypeIndex, paramIndex, { key: e.target.value })} placeholder={t.paramKey} />
                  <input value={param.label} onChange={(e) => updateParam(selectedTypeIndex, paramIndex, { label: e.target.value })} placeholder={t.paramLabel} />
                  <select value={param.inputType || "text"} onChange={(e) => updateParam(selectedTypeIndex, paramIndex, { inputType: e.target.value })}>
                    {AI_REPORT_PARAM_INPUT_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>{t.paramTypes?.[type.value] || type.label}</option>
                    ))}
                  </select>
                  <input value={param.placeholder} onChange={(e) => updateParam(selectedTypeIndex, paramIndex, { placeholder: e.target.value })} placeholder={t.paramPlaceholder} />
                  <input value={param.defaultValue || ""} onChange={(e) => updateParam(selectedTypeIndex, paramIndex, { defaultValue: e.target.value })} placeholder="Default value" />
                  <input value={serializeOptions(param.options)} onChange={(e) => updateParam(selectedTypeIndex, paramIndex, { options: normalizeAiReportParamOptions(e.target.value) })} placeholder={t.paramOptions} />
                  <label className={`system-config-check ${param.required !== false ? "is-required" : "is-optional"}`}>
                    <input type="checkbox" checked={param.required !== false} onChange={(e) => updateParam(selectedTypeIndex, paramIndex, { required: e.target.checked })} />
                    {param.required !== false ? t.required : t.optional || "Optional"}
                  </label>
                  <label className={`system-config-check ${param.system === true ? "is-required" : "is-optional"}`}>
                    <input type="checkbox" checked={param.system === true} onChange={(e) => updateParam(selectedTypeIndex, paramIndex, { system: e.target.checked })} />
                    System
                  </label>
                  <button type="button" className="system-config-icon-btn" onClick={() => deleteParam(selectedTypeIndex, paramIndex)} aria-label={t.deleteParam}>
                    <DeleteOutlineIcon />
                  </button>
                </div>
              ))}
              {selectedType.params.length === 0 ? <div className="system-config-type-empty">{t.noParams}</div> : null}
            </div>

            <div className="system-config-danger-row">
              <button type="button" className="system-config-delete-btn" onClick={() => deleteType(selectedTypeIndex)}>
                <DeleteOutlineIcon /> {t.deleteType}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  </>
);

export default ReportsConfigPanel;
